import React from 'react';

import { TaskInventory, DynamicCapacityValuesOptions, UserType, VehicleType } from '@bringg/types';
import { Tooltip, ValueBar } from '@bringg/react-components';
import type { Dictionary } from 'lodash';
import { calculateTaskInventoriesMaxLoad } from '@bringg-frontend/shared-tasks-weight';

import { addDecimal, roundNumber } from '../../../../../../utils/numbers-utils';
import { RoutesPlannerRun, RoutesPlannerTask } from '../../../../../../types';
import {
	buildDynamicAggregationHintText,
	DynamicAggregatedValues,
	getDynamicAggregationValues
} from '../../../../../../services/dynamic-aggregations/dynamic-aggregations';
import { NO_ROUTE_ID } from '../../../../../../stores/routes-planner-store';
import { RouteRowTranslations } from '../../../../../routes-planner/translations';

interface Props {
	isRunModified: (runId: number) => boolean;
	run: RoutesPlannerRun;
	runId: number;
	tasksInventories: TaskInventory[];
	ignoreQuantityInLoadCalculation: boolean;
	uniqueInventoryFields: string[];
	dynamicCapacityMode: DynamicCapacityValuesOptions;
	getUserType: (run: Partial<RoutesPlannerRun>, tasks: RoutesPlannerTask[], routes: RoutesPlannerRun[]) => UserType;
	routesById: Dictionary<RoutesPlannerRun>;
	subRows: any;
	vehicleTypeByVehicleIds: Dictionary<VehicleType>;
	enableVehicles: boolean;
	translations: RouteRowTranslations;
}

const WeightCell = ({
	isRunModified,
	runId,
	run,
	tasksInventories,
	ignoreQuantityInLoadCalculation,
	uniqueInventoryFields,
	dynamicCapacityMode,
	routesById,
	getUserType,
	subRows,
	vehicleTypeByVehicleIds,
	enableVehicles,
	translations
}: Props) => {
	const validForRunAggregations = !isRunModified(runId);

	const getUserTypeMaxWeight = () => {
		return getUserType(
			routesById[runId],
			subRows.map(task => task.original),
			Object.values(routesById)
		)?.max_total_weight;
	};

	const getVehiclesCapacity = () => {
		if (runId === NO_ROUTE_ID) {
			return getUserTypeMaxWeight();
		}

		const vehicleId = routesById[runId].vehicle_id;
		const trailersIds = subRows.map(task => task.original.vehicle_id);
		let vehicleTotalWeight = 0;

		const uniqVehiclesIds = [...new Set([vehicleId, ...trailersIds])];

		uniqVehiclesIds.forEach(vehicleId => {
			vehicleTotalWeight += vehicleTypeByVehicleIds[vehicleId]?.max_total_weight || 0;
		});
		return vehicleTotalWeight || getUserTypeMaxWeight();
	};

	const maxWeightInRun =
		validForRunAggregations && run?.max_weight
			? run.max_weight
			: calculateTaskInventoriesMaxLoad(
					tasksInventories,
					'weight',
					ignoreQuantityInLoadCalculation,
					uniqueInventoryFields
			  );

	const dynamicAggregatedValues: DynamicAggregatedValues = getDynamicAggregationValues(
		run,
		dynamicCapacityMode,
		'weight'
	);

	const weightData = {
		maxWeightInRun,
		totalWeightCapacity:
			validForRunAggregations && run?.weight_capacity
				? run.weight_capacity
				: enableVehicles
				? getVehiclesCapacity()
				: getUserTypeMaxWeight(),
		dynamicAggregatedValues
	};

	const highestWeightInRun = +addDecimal(weightData.maxWeightInRun);
	let totalVehicleWeight = 0;

	if (weightData?.totalWeightCapacity > 0) {
		totalVehicleWeight = +addDecimal(weightData.totalWeightCapacity);
	}
	const { currentLoad, expectedPickup } = translations;
	const dynamicAggregationTooltip = buildDynamicAggregationHintText(weightData.dynamicAggregatedValues || {}, {
		currentLoad,
		expectedPickup
	});

	const tooltipText1 = totalVehicleWeight ? `${roundNumber((highestWeightInRun / totalVehicleWeight) * 100)}%` : '';
	const tooltipText2 = dynamicAggregationTooltip?.length ? dynamicAggregationTooltip : '';

	const tooltipText = `${tooltipText1} \n ${tooltipText2}`;

	return (
		<Tooltip
			key="route-row-total-weight"
			title={tooltipText}
			placement="top"
			overlayStyle={{ whiteSpace: 'pre-line' }}
			overlayClassName="route-row-total-weight"
		>
			{totalVehicleWeight ? (
				<div data-test-id="weight-value-bar">
					<ValueBar
						value={highestWeightInRun}
						total={totalVehicleWeight}
						strokeColor={'#FFCB00'}
						className="weight-bar"
					/>
				</div>
			) : (
				highestWeightInRun
			)}
		</Tooltip>
	);
};

export default WeightCell;
